if (!customElements.get('native-product-bundle')) {
  class NativeProductBundle extends HTMLElement {
    connectedCallback() {
      if (this.loaded) return;
      this.controller?.abort();
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.load(signal).catch((error) => {
        if (signal.aborted) return;
        this.hidden = true;
        console.warn('Unable to load bundle components:', error);
      });
    }

    disconnectedCallback() {
      this.controller?.abort();
    }

    async requestWithRetry(url, options, readResponse) {
      // Retry only temporary failures, at most twice per request. Successful
      // component cards are retained while other cards retry independently.
      for (let attempt = 0; attempt < 3; attempt++) {
        options.signal?.throwIfAborted();
        try {
          const response = await fetch(url, options);
          if (!response.ok) {
            const error = new Error(`Bundle request failed (HTTP ${response.status})`);
            error.retryable = response.status === 408 || response.status === 429 || response.status >= 500;
            error.retryAfter = response.headers?.get('Retry-After');
            throw error;
          }
          return await readResponse(response);
        } catch (error) {
          if (options.signal?.aborted || error.name === 'AbortError' || attempt === 2) throw error;
          if (!error.retryable && !(error instanceof TypeError)) throw error;

          let delay = 500 * 2 ** attempt;
          if (error.retryAfter) {
            const seconds = Number(error.retryAfter);
            const retryAfter = Number.isFinite(seconds)
              ? seconds * 1000
              : Date.parse(error.retryAfter) - Date.now();
            if (Number.isFinite(retryAfter)) delay = Math.max(delay, retryAfter);
          }
          await this.waitForRetry(Math.min(delay, 30000), options.signal);
        }
      }
    }

    waitForRetry(delay, signal) {
      return new Promise((resolve, reject) => {
        const abort = () => {
          clearTimeout(timer);
          signal.removeEventListener('abort', abort);
          reject(signal.reason);
        };
        const timer = setTimeout(() => {
          signal?.removeEventListener('abort', abort);
          resolve();
        }, delay);
        signal?.addEventListener('abort', abort, { once: true });
        if (signal?.aborted) abort();
      });
    }

    async getComponents(signal) {
      // Native components belong to a variant, not a product type or metafield.
      const query = `query BundleComponents(
        $id: ID!, $after: String, $country: CountryCode!, $language: LanguageCode!
      ) @inContext(country: $country, language: $language) {
        node(id: $id) {
          ... on ProductVariant {
            components(first: 50, after: $after) {
              nodes {
                quantity
                productVariant { id product { id handle } }
              }
              pageInfo { hasNextPage endCursor }
            }
          }
        }
      }`;
      const components = [];
      let after = null;

      do {
        const result = await this.requestWithRetry('/api/2026-10/graphql.json', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          signal,
          body: JSON.stringify({
            query,
            variables: {
              id: `gid://shopify/ProductVariant/${this.dataset.variantId}`,
              after,
              country: this.dataset.country,
              language: this.dataset.language,
            },
          }),
        }, async (response) => {
          const result = await response.json();
          // Shopify can report throttling with an HTTP 200 response.
          if (result.errors?.length && result.errors.every((error) =>
            ['THROTTLED', 'INTERNAL_SERVER_ERROR'].includes(error.extensions?.code)
          )) {
            const error = new Error('Bundle API temporarily unavailable');
            error.retryable = true;
            throw error;
          }
          return result;
        });
        const connection = result.data?.node?.components;
        if (result.errors?.length || !connection) throw new Error('Bundle component data unavailable');
        components.push(...connection.nodes);
        after = connection.pageInfo.hasNextPage ? connection.pageInfo.endCursor : null;
      } while (after);

      return components;
    }

    async getCard(component, signal) {
      const variant = component.productVariant;
      const root = this.dataset.rootUrl.replace(/\/?$/, '/');
      const url = new URL(`${root}products/${encodeURIComponent(variant.product.handle)}`, window.location.origin);
      url.searchParams.set('variant', variant.id.split('/').pop());
      url.searchParams.set('section_id', 'bundle-component-card');

      // Render through Liquid to retain localized money, images and reviews.
      const text = await this.requestWithRetry(url, { signal }, (response) => response.text());
      const html = new DOMParser().parseFromString(text, 'text/html');
      const card = html.querySelector('.bundle-card');
      if (!card || card.dataset.componentVariantId !== variant.id.split('/').pop()) {
        throw new Error('Bundle component card unavailable');
      }
      if (component.quantity > 1) {
        const quantity = card.querySelector('[data-component-quantity]');
        quantity.textContent = `× ${component.quantity}`;
        quantity.hidden = false;
      }
      return card;
    }

    async load(signal) {
      const components = await this.getComponents(signal);
      if (!components.length) return;
      // Stable sort: current product first, Shopify's order for everything else.
      components.sort((a, b) =>
        Number(b.productVariant.product.id === this.dataset.currentProductId) -
        Number(a.productVariant.product.id === this.dataset.currentProductId)
      );
      const cards = await Promise.all(components.map((component) => this.getCard(component, signal)));
      if (signal.aborted || !this.isConnected) return;

      const items = document.createDocumentFragment();
      const divider = this.querySelector('[data-component-divider]');
      cards.forEach((card, index) => {
        if (index) items.append(divider.content.cloneNode(true));
        items.append(card);
      });
      items.append(this.querySelector('[data-total-divider]').content.cloneNode(true));
      this.querySelector('[data-bundle-components]').replaceChildren(items);
      this.toggleAttribute('data-single-component', cards.length === 1);
      this.toggleAttribute('data-multiple-components', cards.length > 2);
      this.loaded = true;
      this.hidden = false;
    }
  }

  customElements.define('native-product-bundle', NativeProductBundle);
}
